import {
  Tooltip,
  useColorModeValue,
  useToast,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  useDisclosure,
  Flex,
  Divider,
  Button,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import {
  ThreadGQLType,
  useCreateNewCommentMutation,
  useCreateThreadActionMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { CommentBox } from './CommentBox';
import {
  CommentEditorInput,
  CommentEditorCancelButton,
  CommentEditorCreateButton,
  CommentEditor,
} from './CommentEditor';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';

// Hooks
const useThreadPermissions = (thread: ThreadGQLType) => {
  const { user } = useUserAuthOrThrow();
  return useMemo(() => {
    const allOrganizations = thread.threadPermissions.map((org) => ({
      id: org.organizationId,
      slug: org.organizationSlug,
      name: org.organizationName,
    }));
    const externalOrganizations = allOrganizations.filter(
      (org) => org.id !== user.organization.id,
    );
    const isParticipating =
      allOrganizations.length !== externalOrganizations.length;

    return {
      isParticipating,
      allOrganizations,
      externalOrganizations,
    };
  }, [thread.threadPermissions, user.organization.id]);
};

// Fragments
type PinnedWrapperProps = {
  'data-testid'?: string;
  thread: ThreadGQLType;
  children: React.ReactNode;
};
const PinnedWrapper = ({
  'data-testid': testId = 'PinnedWrapper',
  children,
  thread,
}: PinnedWrapperProps) => {
  const pinnedBgColor = useColorModeValue('gray.50', 'gray.700');
  if (!thread.pinned) return <>{children}</>;
  return (
    <Card
      data-testid={testId}
      variant="outline"
      background={pinnedBgColor}
      width="full"
    >
      <CardBody display="flex" flexDirection="column" gap="1rem" padding="1rem">
        <Typography variant="detail" fontWeight="bold">
          {thread.latestActionLog?.actionType === 'pin'
            ? `Pinned by ${thread.latestActionLog.userFirstName} ${thread.latestActionLog.userLastName}`
            : 'Pinned'}
        </Typography>
        {children}
      </CardBody>
    </Card>
  );
};

type PinThreadButtonProps = { thread: ThreadGQLType; onClick: () => void };
const PinThreadButton = ({ thread, onClick }: PinThreadButtonProps) => {
  const { user } = useUserAuthOrThrow();
  const toast = useToast();
  const [createThreadActionMutation] = useCreateThreadActionMutation();
  if (thread.resolved) return null;
  return (
    <IconButtonV2
      aria-label={thread.pinned ? 'Unpin Thread' : 'Pin Thread'}
      variant="primary-muted"
      colorScheme="gray"
      isActive={thread.pinned}
      size="sm"
      icon={<Icon icon="thumbtack" />}
      onClick={async () => {
        try {
          await createThreadActionMutation({
            variables: {
              threadId: thread.threadId,
              userId: user.id,
              action: thread.pinned ? 'unpin' : 'pin',
            },
          });
          onClick();
        } catch (error) {
          toast({
            title: thread.pinned
              ? 'Failed to unpin thread'
              : 'Failed to pin thread',
            status: 'error',
            position: 'top',
          });
        }
      }}
    />
  );
};

type ResolveThreadButtonProps = { thread: ThreadGQLType; onClick: () => void };
const ResolveThreadButton = ({ thread, onClick }: ResolveThreadButtonProps) => {
  const { user } = useUserAuthOrThrow();
  const toast = useToast();
  const [createThreadActionMutation] = useCreateThreadActionMutation();
  return (
    <IconButtonV2
      aria-label={thread.resolved ? 'Reopen Thread' : 'Resolve Thread'}
      variant="primary-muted"
      colorScheme="gray"
      isActive={thread.resolved}
      size="sm"
      icon={<Icon icon="circle-check" />}
      onClick={async () => {
        try {
          await createThreadActionMutation({
            variables: {
              threadId: thread.threadId,
              userId: user.id,
              action: thread.resolved ? 'open' : 'resolve',
            },
          });
          onClick();
        } catch (error) {
          toast({
            title: thread.resolved
              ? 'Failed to reopen thread'
              : 'Failed to resolve thread',
            status: 'error',
            position: 'top',
          });
        }
      }}
    />
  );
};

type ExternalPartyAlertProps = { thread: ThreadGQLType };
const ExternalPartyAlert = ({ thread }: ExternalPartyAlertProps) => {
  const { externalOrganizations } = useThreadPermissions(thread);
  if (externalOrganizations.length === 0) return null;
  return (
    <Tooltip
      label={externalOrganizations.map((org) => org.name).join(', ')}
      placement="top-start"
    >
      <Alert status="info">
        <AlertIcon />
        {externalOrganizations.length} External{' '}
        {externalOrganizations.length > 1 ? 'Parties' : 'Party'}
      </Alert>
    </Tooltip>
  );
};

type ToggleDividerProps = {
  'data-testid'?: string;
  children: React.ReactNode;
  onClick: () => void;
};
const ToggleDivider = ({
  'data-testid': testId = 'ToggleDivider',
  children,
  onClick,
}: ToggleDividerProps) => (
  <Flex
    data-testid={testId}
    alignItems="center"
    justifyContent="flex-end"
    gap="0.5rem"
  >
    <Divider />
    <Button
      data-testid={`${testId}.Button`}
      flexShrink={0}
      size="sm"
      variant="link"
      textDecoration="none"
      colorScheme="gray"
      onClick={onClick}
    >
      {children}
    </Button>
  </Flex>
);

// Exports
type CommentThreadProps = {
  'data-testid'?: string;
  thread: ThreadGQLType;
  onCreateNewComment?: (newCommentId: string) => void;
  children?: React.ReactNode;
  refetchThread?: () => void;
};
export const CommentThread = ({
  'data-testid': testId = 'CommentThread',
  thread,
  onCreateNewComment = () => {},
  children,
  refetchThread = () => {},
}: CommentThreadProps) => {
  const { isParticipating } = useThreadPermissions(thread);
  const { user } = useUserAuthOrThrow();
  const toast = useToast();
  const commentList = useDisclosure({ defaultIsOpen: false });
  const [createNewCommentMutation] = useCreateNewCommentMutation();

  const onCreateComment = async (comment: string) => {
    try {
      const result = await createNewCommentMutation({
        variables: {
          threadId: thread.threadId,
          comment,
          authorUserId: user.id,
        },
      });
      if (!result.data?.newComment) {
        throw Error(
          'Missing [newCommentData] when executing [createNewCommentMutation]',
        );
      }
      onCreateNewComment(result.data.newComment); // this is the id of the comment that was created (see resolver)
      commentList.onOpen();
      refetchThread();
    } catch (error) {
      toast({
        title: 'Failed to create comment. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    }
  };

  const [firstComment, ...restComments] = thread.comments;
  if (!firstComment || !isParticipating) return null;

  const commentCountText =
    restComments.length === 1 ? '1 comment' : `${restComments.length} comments`;

  // Pinned threads are wrapped in an extra <Card />
  return (
    <PinnedWrapper data-testid={`${testId}.PinnedWrapper`} thread={thread}>
      <Card data-testid={testId} variant="outline" width="full">
        <CardBody
          display="flex"
          flexDirection="column"
          gap="1rem"
          padding="1rem"
        >
          <ExternalPartyAlert thread={thread} />
          {children}
          <CommentBox
            comment={firstComment}
            actions={
              <>
                <PinThreadButton thread={thread} onClick={refetchThread} />
                <ResolveThreadButton thread={thread} onClick={refetchThread} />
              </>
            }
          />
          {restComments.length > 0 && (
            <>
              <ToggleDivider
                data-testid={`${testId}.ToggleDivider`}
                onClick={commentList.onToggle}
              >
                {commentList.isOpen
                  ? `Hide ${commentCountText}`
                  : `Show ${commentCountText}`}
              </ToggleDivider>
              {commentList.isOpen && (
                <Flex
                  data-testid={`${testId}.CommentList`}
                  direction="column"
                  gap="1rem"
                >
                  {restComments.map((comment) => (
                    <CommentBox key={comment.id} comment={comment} />
                  ))}
                </Flex>
              )}
            </>
          )}
          {/* Hide reply if thread is resolved. You can only reply to "open" threads. */}
          {!thread.resolved && (
            <CommentEditor
              onCreate={onCreateComment}
              right={
                <>
                  <CommentEditorCancelButton />
                  <CommentEditorCreateButton />
                </>
              }
            >
              <CommentEditorInput isEditable />
            </CommentEditor>
          )}
        </CardBody>
      </Card>
    </PinnedWrapper>
  );
};
